import { Container, Header } from "components/Page"
import { ReportPayload, getExportPayload } from "./utils"
import { generateReport, getCubeApi } from "./api"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { AgGridReact } from "ag-grid-react"
import Breadcrumbs from "components/Breadcrumbs"
import Button from "ui/buttons/Button"
import { ButtonGroup } from "components/buttons/ButtonGroup"
import { CubeProvider } from "@cubejs-client/react"
import { CubejsApi } from "@cubejs-client/core"
import { FDReportingGrid } from "./components/FDReportingGrid"
import MoreActions from "components/MoreActions"
import { ReportingGrid } from "./components/ReportingGrid"
import { Unauthorized } from "components/Unauthorized"
import { css } from "@emotion/css"
import { proxy } from "valtio"
import { useAuthenticated } from "providers/A12n"
import { useI18n } from "providers/I18n"
import { useSendFeedBack } from "components/FeedbackSnackbar"

export const modalStates = proxy({
  saveTemplate: { open: false },
  loadTemplate: { open: false },
})

export function Reporting() {
  const downloadRef = useRef<HTMLAnchorElement | null>(null)
  const [cubeJsApi, setCubeJsApi] = useState<CubejsApi | undefined>()
  const gridRef = useRef<AgGridReact>(null)
  const { t } = useI18n()
  const { meta: { userRole, timeZone = "Etc/UTC" } = {} } = useAuthenticated()
  const feedback = useSendFeedBack()

  const resetState = useCallback(() => {
    gridRef.current!.columnApi.resetColumnState()
    gridRef.current!.api.setFilterModel(null)
    gridRef?.current?.columnApi.setPivotResultColumns([])
    gridRef.current!.columnApi.setPivotMode(false)
  }, [])

  const [exportPayload, setExportPayload] = useState<ReportPayload>()
  const [exporting, setExporting] = useState(false)

  const exportXls = useCallback(
    (exportPayload: ReportPayload) => {
      setExporting(true)
      generateReport(exportPayload)
        .then((res) => {
          const objectUrl = URL.createObjectURL(res.blob)
          if (downloadRef.current) {
            downloadRef.current.href = objectUrl
            downloadRef.current.download = res.filename
            downloadRef.current.click()
          }
          // setDownloading(false)
          return objectUrl
        })
        .then((url) => {
          URL.revokeObjectURL(url)
          setExporting(false)
        })
        .catch((err) => {
          // console.log(err)
          // setDownloading(false)
          setExporting(false)
          feedback(t(err.message), "error")
        })
    },
    [feedback, t, setExporting],
  )

  const onExport = useCallback(() => {
    const isPivotMode = gridRef.current!.columnApi.isPivotMode()
    const columnState = gridRef.current!.columnApi.getColumnState()
    const hasRowGroups = columnState.find((row) => row.rowGroup)
    //call export service only for flat exports
    if (isPivotMode || hasRowGroups) {
      gridRef.current!.api.exportDataAsExcel()
      return
    }
    const payload = getExportPayload(exportPayload!, columnState, timeZone)
    !!payload && exportXls(payload)
  }, [exportPayload, timeZone, exportXls])

  const exportDisabled =
    exporting ||
    gridRef.current?.columnApi.isPivotMode() ||
    !!gridRef.current?.columnApi.getColumnState().find((row) => row.rowGroup)

  const reportingGrid = useMemo(() => {
    const commonProps = {
      gridRef,
      modalStates,
      resetState,
      setExportPayload,
    }
    if (
      userRole === "GLOBAL_FINANCE" ||
      userRole === "MARKET_FINANCE" ||
      userRole === "REGIONAL_FINANCE"
    ) {
      return <FDReportingGrid {...commonProps} />
    }
    return <ReportingGrid {...commonProps} userRole={userRole} />
  }, [resetState, userRole])

  useEffect(() => {
    async function _setCubeJSApi() {
      const response = await getCubeApi()
      setCubeJsApi(response)
    }
    _setCubeJSApi()
  }, [])

  if (!cubeJsApi) return <Unauthorized />

  return (
    <CubeProvider cubejsApi={cubeJsApi}>
      <Container>
        <Breadcrumbs
          current={t("reporting.create_report")}
          items={[
            {
              backLink: `${t("nav.reports")}`,
              to: "/reporting",
            },
          ]}
        />
        <Header label={t("reporting.create_report")}>
          <a
            ref={downloadRef}
            className="visually-hidden"
            download={"report.xlsx"}
            href={"#ohno"}
          >
            export
          </a>
          <ButtonGroup>
            <Button
              variant="outlined"
              onClick={() => (modalStates.loadTemplate.open = true)}
            >
              {t("reporting.templates")}
            </Button>
            <Button
              color="primary"
              variant="contained"
              onClick={onExport}
              disabled={exportDisabled}
            >
              {t("reporting.export-report")}
            </Button>
            <MoreActions
              options={[
                {
                  label: t("reporting.save-template"),
                  onClick: () => (modalStates.saveTemplate.open = true),
                },
                {
                  label: t("reporting.reset-template"),
                  labelStyle: styles.danger,
                  onClick: resetState,
                },
              ]}
            />
          </ButtonGroup>
        </Header>
        {reportingGrid}
      </Container>
    </CubeProvider>
  )
}

const styles = {
  danger: css`
    * {
      color: var(--red) !important;
    }
  `,
}
